import { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { Submit } from "../../../../components/FormInputs/Submit";
import { OnSubmitValidationError } from "../../../../utils/OnSubmitValidationError";
import { navigate } from "@reach/router";
import { showToast } from "../../../../utils/helpers";
import AuthContainer from "newui/features/Auth/components/AuthContainer";
import UsernamePassword, { createPasswordSchema, passwordConfirmationSchema } from "newui/features/Auth/components/Login/components/UsernamePassword";
import { ReturnToLoginButton } from "newui/features/Auth/components/ReturnToLoginButton";
import { FlashError } from "components/FormInputs/FlashError";
import { InfoBox } from "newui/components/IconBox";
import { resetPassword } from "../auth-api";

export const PasswordResetForm = ({ path: _path }) => {
	const { search } = window.location;
	const params = new URLSearchParams(search);
	const email = params.get("email")?.replace(" ", "+");
	const token = params.get("token");
	const [errorMessage, setErrorMessage] = useState(null);
	const [showPasswordForm, setShowPasswordForm] = useState(true);

	useEffect(() => {
		if (token == null || email == null) navigate("/login", { replace: true });
	}, []); // eslint-disable-line

	const handleSubmit = async (values, setErrors, setSubmitting) => {
		setSubmitting(true);

		try {
			const resetResponse = await resetPassword(values);

			if (resetResponse?.status) {
				setShowPasswordForm(false);
			} else {
				setErrorMessage(resetResponse?.message ?? 'An unexpected error occurred, please try again.');
			}
		} catch (e) {
			const error = await e.response.json();
			showToast(error.message, "Error");
			error.errors !== undefined && setErrors(error.errors);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<AuthContainer additionalHeadingText={''} welcomeOverrideText="Reset Your Password">
			<p className="mb-4">
				Enter your new password below and submit. You will then be
				redirected to the login page
			</p>

			{showPasswordForm ? <Formik
				enableReinitialize
				initialValues={{
					token: token || "",
					email: email || "",
					password: "",
					password_confirmation: "",
				}}
				validationSchema={Yup.object({
					email: Yup.string()
						.email()
						.required("* This Field is required"),
					password: createPasswordSchema,
					password_confirmation: passwordConfirmationSchema,
				})}
				onSubmit={(values, { setErrors, setSubmitting }) => {
					handleSubmit(values, setErrors, setSubmitting);
				}}
			>
				{(props) => {
					const { setFieldValue, isSubmitting } = props;

					const onSubmitValidationError = () => {
						showToast("Please review field errors in form.", "Error");
					};

					return (
						<Form>
							<div className="row">
								<UsernamePassword emailDataTestid='email' isSignUp passwordConfirmationDataTestid='password-confirmation-reset' passwordDataTestid='password-reset' setFieldValue={setFieldValue} />

								{errorMessage && <FlashError
									heading={'Error'}
									text={errorMessage}
									margin={2}
								/>}

								<div className="col xl-12 mt-2">
									<Submit
										type="submit"
										widthExpand
										isSubmitting={isSubmitting}
										text="Submit"
										submittingText="Submitting in..."
									/>
								</div>
							</div>

							<OnSubmitValidationError
								callback={onSubmitValidationError}
							/>
						</Form>
					);
				}}
			</Formik>
				:
				<div className="mb-8">
					<InfoBox>
						Your password was successfully reset - please return to the login screen to login with your new password.
					</InfoBox>
				</div>}
			<ReturnToLoginButton isLoading={false} />
		</AuthContainer>
	);
};
